export const CUSTOM_BUTTON_LABEL_REQUIRED_MESSAGE = '퀘스트 버튼 문구를 입력해주세요';

export const HIDDEN_QUEST_REVEAL_REQUIRED_MESSAGE =
  '다음 퀘스트가 숨김 상태이므로 퀘스트 버튼을 표시해야 합니다';

const REVEAL_QUEST_COMMAND_TYPE = 'RealWorld.Commands.RevealQuest';

interface ValidateCustomButtonLabelParams {
  isCustomizeButtonText?: boolean;
  label?: string | null;
}

interface ValidateHiddenQuestRevealParams {
  missions?: Mission[];
  questId?: number;
  isVisibleDefault?: boolean;
}

interface ValidateHiddenNextQuestRevealParams {
  missions?: Mission[];
  currentQuestId?: number;
  reactionDefinitions?: ReactionDefinition[];
  failureCommands?: ReactionCommand[];
}

export function validateCustomButtonLabel({
  isCustomizeButtonText,
  label
}: ValidateCustomButtonLabelParams): string | undefined {
  if (!isCustomizeButtonText) return undefined;

  return label?.trim() ? undefined : CUSTOM_BUTTON_LABEL_REQUIRED_MESSAGE;
}

export function validateHiddenQuestReveal({
  missions,
  questId,
  isVisibleDefault
}: ValidateHiddenQuestRevealParams): string | undefined {
  if (isVisibleDefault !== false || !isValidId(questId)) return undefined;

  return hasRevealQuestCommandInMissions(missions, questId)
    ? undefined
    : HIDDEN_QUEST_REVEAL_REQUIRED_MESSAGE;
}

export function validateHiddenNextQuestReveal({
  missions,
  currentQuestId,
  reactionDefinitions,
  failureCommands
}: ValidateHiddenNextQuestRevealParams): string | undefined {
  if (!isValidId(currentQuestId)) return undefined;

  const nextQuest = findNextQuest(missions, currentQuestId);
  if (!nextQuest || nextQuest.isVisibleDefault) return undefined;

  const commands = getCommandsFromReactionDefinitions(reactionDefinitions).concat(
    failureCommands ?? []
  );

  return hasRevealQuestCommand(commands, nextQuest.id)
    ? undefined
    : HIDDEN_QUEST_REVEAL_REQUIRED_MESSAGE;
}

function findNextQuest(missions: Mission[] | undefined, currentQuestId: number): Quest | undefined {
  const mission = missions?.find((mission) =>
    mission.quests.some((quest) => quest.id === currentQuestId)
  );
  if (!mission) return undefined;

  const sortedQuests = [...mission.quests].sort((a, b) => a.order - b.order);
  const currentQuestIndex = sortedQuests.findIndex((quest) => quest.id === currentQuestId);

  return currentQuestIndex === -1 ? undefined : sortedQuests[currentQuestIndex + 1];
}

function hasRevealQuestCommandInMissions(
  missions: Mission[] | undefined,
  questId: number
): boolean {
  return (
    missions?.some((mission) =>
      mission.quests.some((quest) => {
        const { actionHandler } = quest;
        const commands = getCommandsFromReactionDefinitions(
          actionHandler?.reactionDefinitions
        ).concat(actionHandler?.failureCommands ?? []);

        return hasRevealQuestCommand(commands, questId);
      })
    ) ?? false
  );
}

function getCommandsFromReactionDefinitions(
  reactionDefinitions: ReactionDefinition[] | undefined
): ReactionCommand[] {
  return (reactionDefinitions ?? []).reduce<ReactionCommand[]>(
    (commands, reactionDefinition) => commands.concat(reactionDefinition.commands ?? []),
    []
  );
}

function hasRevealQuestCommand(commands: ReactionCommand[], questId: number): boolean {
  return commands.some(
    (command) =>
      command.type === REVEAL_QUEST_COMMAND_TYPE &&
      normalizeId(command.parameters?.questId) === questId
  );
}

function isValidId(id: number | undefined): id is number {
  return typeof id === 'number' && Number.isFinite(id);
}

function normalizeId(value: unknown): number | undefined {
  const id = Number(value);

  return Number.isFinite(id) ? id : undefined;
}
